import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate as useRouterNavigate, type Location } from 'react-router';

import { excuseNextLeave } from '@/lib/editGuard/store';
import { bookState, stateFor, type BookState } from '@/lib/navigation';

/** What a move may say about itself. */
export interface MoveOptions {
  /** The voice turned the page, not the reader. */
  byVoice?: boolean;
  /** Going up from this section: the page above lands on its row. */
  land?: { section: string };
}

export interface BookNavigation {
  /** Into a page: a push, or Back when that page is the entry just behind. */
  open: (to: string, options?: MoveOptions) => void;
  /** To a page beside this one (previous, next, another section): replaces this entry. */
  sibling: (to: string, options?: MoveOptions) => void;
  /** To the page above: Back when it is the entry just behind, else a push that lands on the row left. */
  up: (to: string, options?: MoveOptions) => void;
  /** Away from a page that is gone: Back when the page above is behind, else a replace. */
  leave: (to: string) => void;
}

const withoutHash = (path: string): string => path.split('#')[0]!;

/**
 * True when the entry just behind this one is `to`: the pathname and search
 * this entry recorded when it was pushed. A target without a search matches
 * on its pathname alone, so a landing parameter cleared since does not count.
 */
export function isBehind(location: Pick<Location, 'state'>, to: string): boolean {
  const from = bookState(location).from;
  // A target with a fragment (a heading) is a place the entry behind may not be at: it is pushed, not gone back to.
  if (!from || to.includes('#')) return false;
  const target = withoutHash(to);
  return target.includes('?') ? from === target : from.split('?')[0] === target;
}

/** The book's history policy (WQ6): one name for each kind of move, as on iOS. */
export function useBookNavigation(): BookNavigation {
  const navigate = useRouterNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const go = useCallback(
    (to: string, replace: boolean, extra: Omit<BookState, 'from'>) => {
      const state = stateFor(locationRef.current, replace, Object.keys(extra).length ? extra : undefined);
      void navigate(to, { replace, state });
    },
    [navigate],
  );
  const back = useCallback(() => void navigate(-1), [navigate]);
  const extra = (options?: MoveOptions): Omit<BookState, 'from'> => ({
    ...(options?.byVoice ? { byVoice: true } : {}),
    ...(options?.land ? { land: options.land } : {}),
  });

  return useMemo<BookNavigation>(
    () => ({
      open: (to, options) => (isBehind(locationRef.current, to) ? back() : go(to, false, extra(options))),
      sibling: (to, options) => go(to, true, extra(options)),
      up: (to, options) => (isBehind(locationRef.current, to) ? back() : go(to, false, extra(options))),
      leave: (to) => {
        // The page is gone (a deleted section): its edits have nowhere to go, so the leave guard stands down.
        excuseNextLeave();
        if (isBehind(locationRef.current, to)) back();
        else go(to, true, {});
      },
    }),
    [go, back],
  );
}

export const newEntryKey = (): string => Math.random().toString(36).slice(2, 10);

/** React Router's entry shape in `history.state` (`{ usr, key, idx }`). */
export const routerEntry = (usr: BookState, idx: number) => ({ usr, key: newEntryKey(), idx });

/** True while this tab's history holds nothing before the current entry of the app. */
export const isFirstEntry = (): boolean => (window.history.state?.idx ?? 0) === 0;

/**
 * A section reached as the first page of the tab gets its document placed
 * behind it, so Back and the crumb return to the document at the section's
 * row (§3.3). Rewrites the current entry as the document and pushes the
 * section through the router.
 */
export function seedParent(navigate: (to: string, options: { state: BookState }) => void, docPath: string, sectionId: string, sectionPath: string): void {
  window.history.replaceState(routerEntry({ land: { section: sectionId } }, 0), '', docPath);
  navigate(sectionPath, { state: { from: docPath } });
}

/** `/d/:doc/s/:section`, with whatever search and hash follow. */
export const SECTION_PATH = /^\/d\/([^/?#]+)\/s\/([^/?#]+)/;
